import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useEmbedBridge, type BridgePatient } from '@/composables/useEmbedBridge'

// sessionStorage key holding the opaque portal patient id across a reload. Only
// the id is ever written — see the privacy note in the store doc below.
const STORAGE_KEY = 'lt.activePatient.portalId'

/**
 * Marks a selection that came from a Session App Dock rather than the portal.
 * Deliberately not a uuid so it can never be mistaken for — or sent as — a
 * portal patient id.
 */
const DOCK_ID_PREFIX = 'dock:'

function readPersistedId(): string | null {
  try {
    return globalThis.sessionStorage?.getItem(STORAGE_KEY) ?? null
  } catch {
    return null // storage unavailable (private mode / SSR) — degrade gracefully
  }
}

function persistId(id: string | null): void {
  try {
    if (id === null) globalThis.sessionStorage?.removeItem(STORAGE_KEY)
    else globalThis.sessionStorage?.setItem(STORAGE_KEY, id)
  } catch {
    /* storage unavailable — degrade to in-memory only */
  }
}

/**
 * The patient the therapist is currently working with.
 *
 * Picked on `launch-choose-patient`; every patient-scoped screen afterwards
 * (starting with the home title) reads the active patient from here.
 *
 * We keep a *snapshot* (ident / name / link) so the header still renders if the
 * portal momentarily drops the patient from the caseload, but `activePatient`
 * prefers the live bridge entry when present so name and link status stay fresh —
 * e.g. after `createLink()` resolves and the portal echoes back
 * `patient:link:created`.
 *
 * Reload survival: a browser reload of the iframe wipes the JS heap, which used
 * to lose the selection and strand patient-scoped screens (they bounce to the
 * picker when `hasActivePatient` is false). We therefore persist the *opaque*
 * `portalPatientId` to sessionStorage and rehydrate it on construction. Once the
 * portal re-pushes its caseload we reconcile: adopt the patient if it's still
 * there, drop it if the portal no longer knows it. **Only the opaque id is
 * stored** — never the decrypted name/ident (PII), which is dropped on
 * `vault:locked` and must not linger in storage. The PII snapshot is rehydrated
 * from the live caseload, so storage stays PII-free and consistent with a vault
 * lock. Cleared on `embed:goodbye` (see useEmbedBridge → store.clear()).
 */
export const useActivePatientStore = defineStore('activePatient', () => {
  const restoredId = readPersistedId()
  const portalPatientId = ref<string | null>(restoredId)
  // Snapshot of the selected patient, used as a fallback when the live caseload
  // doesn't contain the patient (e.g. a transient reconnect). Empty after a
  // reload-restore until the caseload reconciliation below fills it in.
  const ident = ref<string>('')
  const name = ref<string>('')
  const linkedLingotalkPatientId = ref<string | null>(null)
  /**
   * Whether that pairing is still live. Part of the snapshot because the
   * snapshot stands in for the live caseload entry during a transient drop, and
   * a stand-in that silently claimed "linked" would re-enable writes the split
   * has already closed off.
   */
  const lingotalkLinkActive = ref<boolean>(false)

  // True only between a reload-restore (id read from storage) and the first
  // caseload reconciliation. While pending, an id the portal doesn't (re)send is
  // treated as stale and dropped; once confirmed we revert to the
  // snapshot-survives-a-transient-drop behaviour above. A fresh pick is already
  // confirmed, so it clears this immediately.
  const awaitingRestore = ref<boolean>(restoredId !== null)

  /** True when the selection came from a dock handoff rather than the portal. */
  const fromDock = ref<boolean>(restoredId?.startsWith(DOCK_ID_PREFIX) === true)

  const { patients, caseloadReceived } = useEmbedBridge()

  /** The active patient, preferring the live caseload entry over the snapshot. */
  const activePatient = computed<BridgePatient | null>(() => {
    if (portalPatientId.value === null) return null
    const live = patients.value.find((p) => p.portalPatientId === portalPatientId.value)
    if (live) return live
    return {
      portalPatientId: portalPatientId.value,
      ident: ident.value,
      name: name.value,
      archived: false,
      linkedLingotalkPatientId: linkedLingotalkPatientId.value,
      lingotalkLinkActive: lingotalkLinkActive.value,
    }
  })

  /** Derived: true iff a patient has been locked in for this session. */
  const hasActivePatient = computed(() => portalPatientId.value !== null)

  function setActivePatient(p: BridgePatient): void {
    portalPatientId.value = p.portalPatientId
    ident.value = p.ident
    name.value = p.name
    linkedLingotalkPatientId.value = p.linkedLingotalkPatientId
    lingotalkLinkActive.value = p.lingotalkLinkActive
    fromDock.value = false
    awaitingRestore.value = false // a fresh pick needs no reconciliation
    persistId(p.portalPatientId)
  }

  /**
   * Seed the patient from a Session App Dock instead of from the portal.
   *
   * There is no caseload here and no portal patient id: the Videosprechstunde
   * never learns who the session is about, and the handoff carries only the
   * LingoTalk patient the dock is scoped to. So the id below is a local marker,
   * not a portal identifier — it exists to key the selection and is never sent
   * anywhere. Everything patient-scoped reads `linkedLingotalkPatientId`, which
   * is the id the API actually uses, and the server confines this session to it
   * regardless (ScopeToPatient).
   *
   * `fromDock` then keeps the caseload reconciliation below off this selection:
   * an empty caseload must never clear a patient the portal was never asked
   * about.
   */
  function setDockPatient(lingotalkPatientId: string, displayName: string): void {
    portalPatientId.value = `${DOCK_ID_PREFIX}${lingotalkPatientId}`
    ident.value = ''
    name.value = displayName
    linkedLingotalkPatientId.value = lingotalkPatientId
    // A dock handoff is a live LingoTalk patient reached without the portal at
    // all, so there is no practice pairing to have ended. The session is
    // confined to this patient server-side (ScopeToPatient) either way.
    lingotalkLinkActive.value = true
    fromDock.value = true
    awaitingRestore.value = false
    persistId(portalPatientId.value)
  }

  function clear(): void {
    portalPatientId.value = null
    ident.value = ''
    name.value = ''
    linkedLingotalkPatientId.value = null
    lingotalkLinkActive.value = false
    fromDock.value = false
    awaitingRestore.value = false
    persistId(null)
  }

  // Reconcile a reload-restored selection against the live caseload exactly
  // once, when the portal's first post-reload caseload lands. Runs only while
  // `awaitingRestore` so an in-session transient caseload drop keeps the patient
  // via the snapshot (the whole reason the snapshot exists).
  watch(
    () => [caseloadReceived.value, patients.value] as const,
    () => {
      if (fromDock.value) return // no portal caseload to reconcile against
      if (!awaitingRestore.value || !caseloadReceived.value || portalPatientId.value === null) return
      const live = patients.value.find((p) => p.portalPatientId === portalPatientId.value)
      if (live) {
        // Fill the PII snapshot from the live entry so a later transient
        // vault-lock still renders the name, exactly as a fresh pick would.
        ident.value = live.ident
        name.value = live.name
        linkedLingotalkPatientId.value = live.linkedLingotalkPatientId
        lingotalkLinkActive.value = live.lingotalkLinkActive
      } else {
        // The portal no longer knows this patient (archived/removed, or a new
        // session that never sent goodbye). Drop it so screens fall back to the
        // picker instead of operating on a blank patient.
        clear()
      }
      awaitingRestore.value = false
    },
    // Sync flush so the reconciliation completes the instant the caseload state
    // changes — a patient-scoped screen reading the store right after the
    // post-reload caseload lands must never observe a true-but-unreconciled
    // selection (e.g. a stale id that should have bounced it to the picker).
    { immediate: true, deep: true, flush: 'sync' },
  )

  return {
    portalPatientId,
    ident,
    name,
    linkedLingotalkPatientId,
    lingotalkLinkActive,
    activePatient,
    hasActivePatient,
    fromDock,
    setActivePatient,
    setDockPatient,
    clear,
  }
})
